import type { FC, FormEvent, ReactNode } from 'react'
import type { TicketTypeView } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { Lock, Tag, X } from 'lucide-react'
import { calculateDiscount, calculateServiceFee, cms, formatMoney } from '@/lib/format'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input } from '@/components/ui/input'
import { formatTotal, summarizeSelection } from '@/components/events/ticket-selector'

interface AppliedPromo {
    id: string
    code: string
    discountType: string
    discountValue: number
}

interface CheckoutDialogProps {
    open: boolean
    onClose: () => void
    eventId: string
    eventTitle: string
    currency: string
    ticketTypes: Array<TicketTypeView>
    quantities: Record<string, number>
    paymentsReady: boolean
}

export const CheckoutDialog: FC<CheckoutDialogProps> = ({
    open,
    onClose,
    eventId,
    eventTitle,
    currency,
    ticketTypes,
    quantities,
    paymentsReady
}): ReactNode => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [promoInput, setPromoInput] = useState('')
    const [promo, setPromo] = useState<AppliedPromo | null>(null)
    const [promoError, setPromoError] = useState<string | null>(null)
    const [promoLoading, setPromoLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const { lines, count, paidCount, subtotal } = summarizeSelection(ticketTypes, quantities)
    const discount = promo ? calculateDiscount(subtotal, promo.discountType, promo.discountValue) : 0
    const fee = calculateServiceFee(subtotal - discount, paidCount)
    const total = subtotal - discount + fee
    const needsPayment = total > 0

    const applyPromo = async () => {
        const code = promoInput.trim()
        if (!code) return

        setPromoLoading(true)
        setPromoError(null)

        try {
            const response = await fetch('/api/promo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ eventId, code })
            })
            const data = await response.json()

            if (!response.ok) {
                setPromoError(data.error ?? 'That code could not be applied.')
                return
            }

            setPromo(data.promo)
            setPromoInput('')
        }
        catch {
            setPromoError('We could not check that code. Please try again.')
        }
        finally {
            setPromoLoading(false)
        }
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)
        setSubmitting(true)

        try {
            const response = await fetch('/api/checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    eventId,
                    buyerName: name,
                    buyerEmail: email,
                    promoCode: promo?.code ?? null,
                    items: lines.map(line => ({ ticketTypeId: line.id, quantity: line.quantity }))
                })
            })
            const data = await response.json()

            if (!response.ok || !data.url) {
                setError(data.error ?? 'We could not complete your order. Please try again.')
                setSubmitting(false)
                return
            }

            window.location.assign(data.url)
        }
        catch {
            setError('We could not reach the server. Check your connection and try again.')
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} title='Checkout'>
            <form onSubmit={onSubmit} className='flex flex-col gap-6'>
                <p className='-mt-1 text-sm text-muted-foreground'>
                    Tickets for <span className='font-semibold text-foreground' data-mdlfy-cms={cms('events', eventId, 'title')}>{eventTitle}</span>
                </p>

                <div className='flex flex-col gap-4'>
                    <Field label='Full name' htmlFor='checkout-name'>
                        <Input
                            id='checkout-name'
                            name='name'
                            autoComplete='name'
                            required
                            maxLength={100}
                            value={name}
                            onChange={event => setName(event.target.value)}
                        />
                    </Field>

                    <Field label='Email' htmlFor='checkout-email' hint='Your tickets will be sent here.'>
                        <Input
                            id='checkout-email'
                            name='email'
                            type='email'
                            autoComplete='email'
                            required
                            maxLength={200}
                            value={email}
                            onChange={event => setEmail(event.target.value)}
                        />
                    </Field>
                </div>

                <div className='flex flex-col gap-3 rounded-2xl bg-muted/70 p-4'>
                    <ul className='flex flex-col gap-2 text-sm'>
                        {lines.map(line => (
                            <li key={line.id} className='flex justify-between gap-4'>
                                <span>{line.quantity} x <span data-mdlfy-cms={cms('ticket_types', line.id, 'name')}>{line.name}</span></span>
                                <span className='font-medium tabular-nums'>{formatTotal(line.total, currency)}</span>
                            </li>
                        ))}

                        {discount > 0 && promo && (
                            <li className='flex justify-between gap-4 text-success'>
                                <span className='inline-flex items-center gap-1.5'>
                                    <Tag className='size-3.5' aria-hidden='true' />
                                    <span data-mdlfy-cms={cms('promo_codes', promo.id, 'code')}>{promo.code}</span>
                                    <button
                                        type='button'
                                        onClick={() => setPromo(null)}
                                        className='rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground'
                                        aria-label='Remove promo code'
                                    >
                                        <X className='size-3.5' />
                                    </button>
                                </span>
                                <span className='font-medium tabular-nums'>-{formatMoney(discount, currency)}</span>
                            </li>
                        )}

                        {fee > 0 && (
                            <li className='flex justify-between gap-4 text-muted-foreground'>
                                <span>Service fee</span>
                                <span className='tabular-nums'>{formatMoney(fee, currency)}</span>
                            </li>
                        )}
                    </ul>

                    <div className='flex items-center justify-between border-t border-border pt-3'>
                        <span className='font-semibold'>
                            Total for {count} {count === 1 ? 'ticket' : 'tickets'}
                        </span>
                        <span className='font-heading text-xl font-bold tabular-nums'>{formatTotal(total, currency)}</span>
                    </div>
                </div>

                {subtotal > 0 && !promo && (
                    <div className='flex flex-col gap-1.5'>
                        <label htmlFor='checkout-promo' className='text-sm font-medium'>Promo code</label>
                        <div className='flex gap-2'>
                            <Input
                                id='checkout-promo'
                                value={promoInput}
                                onChange={event => setPromoInput(event.target.value.toUpperCase())}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault()
                                        applyPromo()
                                    }
                                }}
                                placeholder='Enter code'
                                maxLength={40}
                                className='font-mono uppercase'
                            />
                            <Button type='button' variant='outline' onClick={applyPromo} loading={promoLoading} disabled={!promoInput.trim()}>
                                Apply
                            </Button>
                        </div>
                        {promoError && <p className='text-sm text-destructive' role='alert'>{promoError}</p>}
                    </div>
                )}

                {needsPayment && !paymentsReady && (
                    <Alert variant='warning' title='Card payments are not switched on yet'>
                        The organizer has not finished setting up payments for paid tickets. Free tickets can still be reserved.
                    </Alert>
                )}

                {error && <Alert variant='error'>{error}</Alert>}

                <div className='flex flex-col gap-3'>
                    <Button type='submit' size='lg' loading={submitting} disabled={count === 0 || (needsPayment && !paymentsReady)}>
                        {needsPayment
                            ? <><Lock className='size-4' aria-hidden='true' /> Continue to payment</>
                            : 'Get my tickets'}
                    </Button>

                    <p className='text-center text-xs leading-relaxed text-muted-foreground'>
                        {needsPayment ? 'You will pay securely on Stripe and come straight back to your tickets. ' : ''}
                        Your name and email are shared with the organizer. See our <Link href='/privacy' className='underline underline-offset-2'>privacy policy</Link>.
                    </p>
                </div>
            </form>
        </Dialog>
    )
}
